/**
 * Componentes del flujo, portados del POC con una regla nueva (H8 de la ronda
 * del plan): acá NO existe innerHTML — todo dato dinámico entra por
 * textContent y los íconos se construyen con createElementNS. El catálogo
 * jamás toca el parser de HTML.
 */

type Atributos = Record<string, string>;

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, atributos?: Atributos, texto?: string): HTMLElementTagNameMap[K] {
	const nodo = document.createElement(tag);
	if (atributos) {
		for (const [clave, valor] of Object.entries(atributos)) {
			if (clave === 'class') nodo.className = valor;
			else nodo.setAttribute(clave, valor);
		}
	}
	if (texto !== undefined) nodo.textContent = texto;
	return nodo;
}

const RE_COLOR = /^#[0-9A-Fa-f]{6}$/;

/** Color del catálogo validado; cualquier otra cosa cae a la paleta fija. */
export function colorSeguro(color: string | null | undefined, alternativa: string): string {
	return color && RE_COLOR.test(color) ? color : alternativa;
}

export const PALETA = ['#1E5FA8', '#C2560F', '#6B3FA0', '#0E7B6C', '#A03A5B', '#5A6B14'];

/** El color de un número de silo o carro; un «—» (catálogo sin la fila) cae al primero, no a `undefined`. */
export function colorDeNumero(texto: string): string {
	const n = Number(texto);
	return PALETA[Number.isFinite(n) ? Math.abs(Math.trunc(n)) % PALETA.length : 0];
}

const SVG_NS = 'http://www.w3.org/2000/svg';

interface Trazo {
	d: string[];
	circulos?: Array<[number, number, number]>;
	rects?: Array<[number, number, number, number, number]>;
	relleno?: string;
}

// `satisfies` y no `Record<string, …>`: así `keyof typeof TRAZOS` son los nombres que existen y el
// tsc rechaza un ícono inventado. Con el Record, `icono('silo')` compilaba y tiraba en la revisión
// del descarte (7/10: la pantalla quedaba en blanco desde el 2/10).
const TRAZOS = {
	check: { d: ['M4 12.5l5.5 5.5L20 6.5'] },
	volver: { d: ['M15 5l-7 7 7 7'] },
	persona: { d: ['M4.8 20a7.2 7.2 0 0114.4 0'], circulos: [[12, 8, 3.6]] },
	lote: { d: ['M3.5 8.5L12 4l8.5 4.5v7L12 20l-8.5-4.5z', 'M3.5 8.5L12 13l8.5-4.5M12 13v7'] },
	carro: {
		d: ['M18 9h2.6l1.4 3v2.5H20'],
		rects: [[3, 6.5, 15, 8, 1.6]],
		circulos: [
			[7.5, 18, 2],
			[15, 18, 2],
		],
	},
	reloj: { d: ['M12 7.4V12l3 2'], circulos: [[12, 12, 8.4]] },
	peso: { d: ['M6 8h12l2.4 11.5H3.6z'], circulos: [[12, 5, 2.2]] },
	bandera: { d: ['M6 21V4M6 4.6h11l-2 3.7 2 3.7H6'] },
	subir: { d: ['M12 19V6M6 11.5L12 5.5l6 6'] },
	camara: {
		d: ['M4 8h3l2-2.5h6L17 8h3a1.5 1.5 0 011.5 1.5V19a1.5 1.5 0 01-1.5 1.5H4A1.5 1.5 0 012.5 19V9.5A1.5 1.5 0 014 8z'],
		circulos: [[12, 13.7, 3.4]],
	},
} satisfies Record<string, Trazo>;

export type NombreDeIcono = keyof typeof TRAZOS;
/** Los nombres que existen, para el test que vigila que ninguna pantalla pida uno inventado. */
export const NOMBRES_DE_ICONO = Object.keys(TRAZOS) as NombreDeIcono[];

export function icono(nombre: keyof typeof TRAZOS, tam = 19, grosor = 2): SVGSVGElement {
	const svg = document.createElementNS(SVG_NS, 'svg');
	svg.setAttribute('width', String(tam));
	svg.setAttribute('height', String(tam));
	svg.setAttribute('viewBox', '0 0 24 24');
	svg.setAttribute('fill', 'none');
	svg.setAttribute('stroke', 'currentColor');
	svg.setAttribute('stroke-width', String(grosor));
	svg.setAttribute('stroke-linecap', 'round');
	svg.setAttribute('stroke-linejoin', 'round');
	// Cinturón: un nombre que no esté en el mapa deja el ícono vacío en vez de tirar y dejar la
	// pantalla entera en blanco (el tsc ya no deja pasar uno inventado; esto cubre lo que no compila acá).
	const forma: Trazo | undefined = (TRAZOS as Record<string, Trazo>)[nombre];
	if (!forma) {
		console.error('icono desconocido', nombre);
		return svg;
	}
	for (const d of forma.d) {
		const path = document.createElementNS(SVG_NS, 'path');
		path.setAttribute('d', d);
		svg.appendChild(path);
	}
	for (const [cx, cy, r] of forma.circulos ?? []) {
		const c = document.createElementNS(SVG_NS, 'circle');
		c.setAttribute('cx', String(cx));
		c.setAttribute('cy', String(cy));
		c.setAttribute('r', String(r));
		svg.appendChild(c);
	}
	for (const [x, y, ancho, alto, radio] of forma.rects ?? []) {
		const rect = document.createElementNS(SVG_NS, 'rect');
		rect.setAttribute('x', String(x));
		rect.setAttribute('y', String(y));
		rect.setAttribute('width', String(ancho));
		rect.setAttribute('height', String(alto));
		rect.setAttribute('rx', String(radio));
		svg.appendChild(rect);
	}
	return svg;
}

export function mil(n: number): string {
	return n.toLocaleString('es-AR');
}

export function kg(gramos: number): string {
	return mil(Math.round(gramos / 1000));
}

/** Tile del POC: avatar/cuadro de color + etiqueta + tilde de selección. */
export function tile(opciones: {
	etiqueta: string;
	sub?: string;
	/** Un renglón más, en verde, debajo del `sub`: lo que se anticipa (a qué pallet va un precinto, §12.18). */
	destacado?: string;
	badge?: string;
	color: string;
	avatarTexto?: string;
	avatarIcono?: keyof typeof TRAZOS;
	avatarCuadrado?: boolean;
	/** Tile bajo (84 px) para las grillas largas —silos, motivos— que si no scrollean dos pantallas. */
	chico?: boolean;
	seleccionado: boolean;
	deshabilitado?: boolean;
	alTocar: () => void;
}): HTMLButtonElement {
	const boton = el('button', { class: opciones.chico ? 'tile chico' : 'tile', 'aria-pressed': opciones.seleccionado ? 'true' : 'false', type: 'button' });
	if (opciones.deshabilitado) boton.setAttribute('disabled', '');
	if (opciones.badge) boton.appendChild(el('span', { class: 'badge' }, opciones.badge));
	const avatar = el('div', { class: opciones.avatarCuadrado ? 'cuadro' : 'av' });
	avatar.style.background = opciones.color;
	if (opciones.avatarTexto !== undefined) avatar.textContent = opciones.avatarTexto;
	if (opciones.avatarIcono) avatar.appendChild(icono(opciones.avatarIcono, 22));
	boton.appendChild(avatar);
	const texto = el('span', undefined, opciones.etiqueta);
	if (opciones.sub) texto.appendChild(el('small', undefined, opciones.sub));
	if (opciones.destacado) texto.appendChild(el('small', { class: 'destacado' }, opciones.destacado));
	boton.appendChild(texto);
	const tick = el('span', { class: 'tick' });
	tick.appendChild(icono('check', 15, 3.4));
	boton.appendChild(tick);
	boton.onclick = opciones.alTocar;
	return boton;
}

export function chip(etiqueta: string, seleccionado: boolean, alTocar: () => void): HTMLButtonElement {
	const boton = el('button', { class: seleccionado ? 'chip sel' : 'chip', type: 'button' }, etiqueta);
	boton.onclick = alTocar;
	return boton;
}

/**
 * El único teclado de las tres estaciones (principio 2 del POC: se toca, no se
 * escribe). Avanzar es siempre el botón verde del pie, nunca una tecla del pad.
 *
 * Última fila como en el POC validado: el CERO ocupa dos columnas y el borrar va
 * a su DERECHA. Estaba al revés, y con el pulgar sobre el 0 la tecla que quedaba
 * al lado era la de borrar en el lugar donde se espera otro dígito. Va en rojo,
 * el mismo de los avisos de detener: es la única tecla que quita algo.
 */
export function teclado(alTocar: (digito: string) => void, alBorrar: () => void, opciones: { coma?: boolean } = {}): HTMLDivElement {
	const grilla = el('div', { class: 'keys' });
	for (const digito of ['1', '2', '3', '4', '5', '6', '7', '8', '9']) {
		const boton = el('button', { class: 'key', type: 'button' }, digito);
		boton.onclick = () => alTocar(digito);
		grilla.appendChild(boton);
	}
	// Calidad tipea decimales: la coma va a la izquierda del cero, y el borrar sigue a
	// la derecha y en rojo, como en el resto de la app.
	if (opciones.coma) {
		const coma = el('button', { class: 'key', type: 'button', 'aria-label': 'Coma decimal' }, ',');
		coma.onclick = () => alTocar(',');
		grilla.appendChild(coma);
	}
	const cero = el('button', { class: opciones.coma ? 'key' : 'key cero', type: 'button' }, '0');
	cero.onclick = () => alTocar('0');
	grilla.appendChild(cero);
	const borrar = el('button', { class: 'key borrar', type: 'button', 'aria-label': 'Borrar' }, '⌫');
	borrar.onclick = alBorrar;
	grilla.appendChild(borrar);
	return grilla;
}

/**
 * Pad anclado (revisión de diseño 7/9, V1, y la segunda pasada del 11/9, X1): la
 * pregunta y el lector quedan fijos arriba, el teclado fijo sobre el pie, y todo lo
 * condicional —avisos, barras, chips— vive en la franja del medio con scroll propio.
 * El pad nunca se mueve bajo el dedo y nunca hay que buscarlo. Es la misma pantalla
 * en verde, producción y calidad. El llamador pone el teclado en `appbody` al final.
 * En un teléfono bajo la franja del medio no alcanza para un cartel con su chip: ahí
 * `.zona` (arriba + medio) pasa a ser lo que hace scroll, y el pad sigue fijo.
 */
export function padAnclado(appbody: HTMLElement, pie: HTMLElement): { arriba: HTMLElement; medio: HTMLElement } {
	appbody.classList.add('con-pad');
	appbody.parentElement?.classList.add('con-pad'); // el .cuerpo que lo contiene
	pie.classList.add('con-pad');
	const zona = el('div', { class: 'zona' });
	const arriba = el('div', { class: 'fijo-arriba' });
	const medio = el('div', { class: 'medio' });
	zona.appendChild(arriba);
	zona.appendChild(medio);
	appbody.appendChild(zona);
	return { arriba, medio };
}

/**
 * Confirmación por mantener apretado (principio 7: con guantes, un toque
 * accidental es un registro fantasma). Pointer Events + latch: una sola
 * ejecución aunque el dispositivo dispare touch y mouse, y sin atajo de
 * teclado instantáneo (H2 de la ronda del plan).
 */
export function botonMantener(texto: string, duracionMs: number, alCompletar: () => void): HTMLButtonElement {
	const boton = el('button', { class: 'btn go hold', type: 'button' });
	const relleno = el('span', { class: 'fill' });
	const etiqueta = el('span', { class: 'holdlab' });
	etiqueta.appendChild(icono('check', 17, 3.4));
	const textoEtiqueta = el('span', undefined, texto);
	etiqueta.appendChild(textoEtiqueta);
	boton.appendChild(relleno);
	boton.appendChild(etiqueta);

	let inicio: number | null = null;
	let intervalo: ReturnType<typeof setInterval> | null = null;
	let completado = false;

	const parar = () => {
		if (intervalo) clearInterval(intervalo);
		intervalo = null;
		inicio = null;
		relleno.style.width = '0%';
		textoEtiqueta.textContent = texto;
	};
	// Progreso por tiempo real con setInterval, no rAF: un rAF pausado (pantalla
	// que se atenúa, battery saver, WebViews raros) colgaría el guardado con el
	// dedo apretado. Soltar sigue cancelando por pointerup/pointercancel.
	const avanzar = () => {
		if (inicio === null || completado) return;
		const progreso = Math.min(1, (Date.now() - inicio) / duracionMs);
		relleno.style.width = `${progreso * 100}%`;
		textoEtiqueta.textContent = progreso < 1 ? 'Seguí apretando' : '¡Listo!';
		if (progreso >= 1) {
			completado = true; // latch: jamás dos commits desde este botón
			parar();
			alCompletar();
		}
	};
	boton.addEventListener('pointerdown', (evento) => {
		evento.preventDefault();
		try {
			boton.setPointerCapture(evento.pointerId);
		} catch {
			// un pointerId inválido no puede impedir el guardado
		}
		if (inicio !== null || completado) return;
		inicio = Date.now();
		intervalo = setInterval(avanzar, 50);
	});
	for (const tipo of ['pointerup', 'pointercancel', 'pointerleave'] as const) {
		boton.addEventListener(tipo, () => {
			if (!completado) parar();
		});
	}
	return boton;
}

export function flag(clase: 'warn' | 'stop' | 'ok', fuerte: string, resto: string): HTMLDivElement {
	const caja = el('div', { class: `flag ${clase}` });
	caja.appendChild(el('b', undefined, fuerte));
	caja.appendChild(document.createTextNode(` ${resto}`));
	return caja;
}
